<template>
  <b-card>
    <!-- form -->
    <b-form v-if="render">
      <layout-loading v-if="itemLoading" />
      <p v-if="!isSuperAdmin" role="note">{{ authorityNotice }}</p>
      <b-row>
        <b-col md="4">
          <b-form-group
            :label="$t('users.fullName')"
            label-for="user-fullName"
            class="mb-2"
          >
            <b-form-input
              id="user-fullName"
              v-model="form.fullName"
              :state="validateState('fullName')"
            />
            <b-form-invalid
              id="input-fullName-feedback"
              :label="$t('users.fullName')"
              :validator="$v.form.fullName"
            />
          </b-form-group>
        </b-col>
        <b-col md="4">
          <b-form-group
            :label="$t('users.phoneNumber')"
            label-for="user-phoneNumber"
            class="mb-2"
          >
            <b-form-input
              id="user-phoneNumber"
              v-model="form.phoneNumber"
              :readonly="true"
            />
          </b-form-group>
        </b-col>
        <b-col v-if="isSuperAdmin" md="4">
          <b-form-group
            :label="$t('users.roles')"
            label-for="user-role"
            class="mb-2"
            :state="validateState('roles')"
          >
            <v-select-custom
              id="user-role"
              v-model="form.roles"
              :options="roleOptions"
              :custom-props="{ multiple: true, 'get-option-label' : option => $t(`users.${option}`), label: 'label' }"
            />
            <b-form-invalid
              id="input-roles-feedback"
              :label="$t('general.roles')"
              :validator="$v.form.roles"
              :is-bootstrap="false"
            />
          </b-form-group>
        </b-col>
        <b-col v-if="canMaintainAccess" md="2">
          <b-form-group
            :label="$t('users.isDeactivate')"
            label-for="user-isDeactivate"
            class="mb-2"
          >
            <b-form-checkbox
              id="user-isDeactivate"
              class="mt-1"
              :checked="form.isDeactivate"
              name="user-isDeactivate"
              switch
              inline
              @input="handleActiveChange"
            />
          </b-form-group>
        </b-col>
        <b-col v-if="canMaintainAccess" md="2">
          <b-form-group
            :label="$t('users.isBlocked')"
            label-for="user-isBlocked"
            class="mb-2"
          >
            <b-form-checkbox
              id="user-isBlocked"
              v-model="form.isBlocked"
              class="mt-1"
              name="user-isBlocked"
              switch
              inline
            />
          </b-form-group>
        </b-col>
        <b-col v-if="canMaintainAccess" md="3">
          <b-form-group
            :label="$t('users.isVerifiedPhoneNumber')"
            label-for="user-isVerifiedPhoneNumber"
            class="mb-2"
          >
            <b-form-checkbox
              id="user-isVerifiedPhoneNumber"
              v-model="form.isVerifiedPhoneNumber"
              class="mt-1"
              name="user-isVerifiedPhoneNumber"
              switch
              inline
            />
          </b-form-group>
        </b-col>
        <b-col
          v-if="isSuperAdmin && isAdminRole"
          md="3"
        >
          <b-form-group
            label="پسوورد جدید"
            label-for="user-newPassword"
            class="mb-2"
          >
            <div class="position-relative">
              <b-form-input
                id="user-newPassword"
                v-model="form.newPassword"
                :type="showPassword ? 'text' : 'password'"
                :placeholder="$t('users.enterNewPassword')"
                :state="form.newPassword ? form.newPassword.length >= 6 : null"
              />
              <div
                class="position-absolute"
                style="top: 50%; left: 20px; transform: translateY(-50%); cursor: pointer;"
                @click="showPassword = !showPassword"
              >
                <feather-icon
                  :icon="showPassword ? 'EyeOffIcon' : 'EyeIcon'"
                  size="16"
                />
              </div>
            </div>
            <small
              v-if="form.newPassword && form.newPassword.length < 6"
              class="text-danger"
            >
              رمز عبور باید حداقل ۶ کاراکتر باشد
            </small>
          </b-form-group>
        </b-col>
        <b-col
          cols="12"
          class="mt-50"
        >
          <b-button
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="primary"
            class="mr-1"
            :disabled="isSubmit"
            @click="submitForm"
          >
            {{ isSubmit ? $t("general.isSubmit") : $t("general.update") }}
          </b-button>
          <b-button
            v-ripple.400="'rgba(186, 191, 199, 0.15)'"
            variant="outline-secondary"
            @click="$router.go(-1)"
          >
            {{ $t("general.back") }}
          </b-button>
        </b-col>
      </b-row>
    </b-form>
    <!--/ form -->
  </b-card>
</template>

<script>
import {
  BButton, BCard, BCol, BForm, BFormCheckbox, BFormGroup, BFormInput, BRow,
} from 'bootstrap-vue'
import BFormInvalid from '@/components/BFormInvalid/index.vue'
import LayoutLoading from '@/components/LayoutLoading.vue'
import Ripple from 'vue-ripple-directive'
import VSelectCustom from '@/components/VSelectCustom/index.vue'
import { required, minLength } from 'vuelidate/lib/validators'
import useUsersList from './useUsersList'
import { authorityMessages, maintenancePayload, ordinaryAccount } from './maintenance-authority'

export default {
  name: 'UsersEdit',
  components: {
    BButton,
    BCard,
    BCol,
    BForm,
    BFormCheckbox,
    BFormGroup,
    BFormInput,
    BFormInvalid,
    BRow,
    LayoutLoading,
    VSelectCustom,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      showPassword: false,
    }
  },
  computed: {
    isSuperAdmin() { return this.$can('manage', 'all') },
    canMaintainAccess() { return this.isSuperAdmin || ordinaryAccount(this.item && this.item.roles) },
    authorityNotice() { return authorityMessages[this.$i18n.locale] || authorityMessages.fa },
    itemId() {
      return this.$router.currentRoute.params.id
    },
    isAdminRole() {
      return this.form.roles && this.form.roles.includes('admin')
    },
    render() {
      if (this.item && Object.keys(this.item).length) {
        return true
      }
      return true
    },
  },
  created() {
    this.fetchItem(this.itemId)
    if (this.isSuperAdmin) this.fetchUserRoles()
  },
  validations: {
    form: {
      fullName: {
        required,
        minLength: minLength(3),
      },
      roles: {
        required,
      },
      isBlocked: {},
      idDeactivate: {},
      newPassword: {
        minLength: minLength(6),
      },
    },
  },
  methods: {
    validateState(name) {
      const {
        $dirty,
        $error,
      } = this.$v.form[name]
      return $dirty ? !$error : null
    },
    submitForm() {
      this.$v.$touch()
      if (this.$v.form.$anyError) {
        this.errorManager('validator.validationError')
        setTimeout(() => document.getElementsByClassName('is-invalid')[0].scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        }), 500)
        return
      }
      this.sendData()
    },
    sendData() {
      const data = maintenancePayload(this.form, this.isSuperAdmin, this.item && this.item.roles)
      this.updateItem(this.itemId, data, this.form.lang)
    },
    handleActiveChange(value) {
      this.$set(this.form, 'isDeactivate', value)
    },
    logFormState() {
      // Method kept but logs removed
    },
  },
  setup() {
    const {
      addItem,
      fetchItem,
      fetchUserRoles,
      form,
      isSubmit,
      item,
      itemLoading,
      roleOptions,
      roles,
      updateItem,
    } = useUsersList()
    return {
      addItem,
      fetchItem,
      fetchUserRoles,
      form,
      isSubmit,
      item,
      itemLoading,
      roleOptions,
      roles,
      updateItem,
    }
  },
}
</script>
